import { Component, OnInit, inject, signal, computed, effect, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DatePickerComponent, DateRangePickerOutput } from '../../../../shared/components/date-picker/date-picker.component';
import { LoaderService } from '../../../../core/services/loader.service';
import { ApiService } from '../../../../core/services/api.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { UnitSuggestionService } from '../../../../core/services/unit-suggestion.service';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { DonutChartComponent } from '../../../../shared/components/charts/donut-chart/donut-chart.component';
import { HorizontalBarChartComponent } from '../../../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';

@Component({
  selector: 'app-operation-recovery',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePickerComponent,
    IndividualToasterComponent,
    DonutChartComponent,
    HorizontalBarChartComponent
  ],
  templateUrl: './operation-recovery.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./operation-recovery.component.scss']
})
export class OperationRecoveryComponent implements OnInit {
  start_date = '';
  end_date = '';
  prev_start_date = '';
  prev_end_date = '';
  comparisonType: any = 'none';
  unit = signal<any>('month');
  allowedGroupByUnits: any[] = ['day', 'week', 'month', 'quarter', 'year'];
  private lastDateRangeKey = '';

  private initialLoadDone = false;

  isLoading = computed(() => {
    return this.isRemindersByChannelLoading() ||
      this.isUpfrontDisbursedLoading() ||
      this.isUpfrontEligibilityLoading() ||
      this.isUpfrontOutcomesLoading() ||
      this.isLateFeesLoading() ||
      this.isDebtRecoveryLoading() ||
      this.isContractStatusLoading() ||
      this.isSupporterCapitalLoading() ||
      this.isSupporterRepaymentLoading() ||
      this.isSupporterFlowLoading();
  });
  isRemindersByChannelLoading = signal(false);
  isUpfrontDisbursedLoading = signal(false);
  isUpfrontEligibilityLoading = signal(false);
  isUpfrontOutcomesLoading = signal(false);
  isLateFeesLoading = signal(false);
  isDebtRecoveryLoading = signal(false);
  isContractStatusLoading = signal(false);
  private loaderService = inject(LoaderService);
  private apiService = inject(ApiService);

  constructor() {
    effect(() => {
      // Must read the signal synchronously here so Angular registers the dependency
      const loading = this.isLoading();

      // Wait until dates are populated (first fetch initiated)
      if (!this.start_date || !this.end_date) return;

      if (!loading && !this.initialLoadDone) {
        this.loaderService.hide();
        this.initialLoadDone = true;
      }
    });
  }
  private productConfigService = inject(ProductConfigService);
  private unitService = inject(UnitSuggestionService);

  lateFeesData = signal<{ totalLateFees: number; instances: number }>({ totalLateFees: 0, instances: 0 });
  debtRecoveryData = signal<{ totalDebtRecovered: number; paidOffSummary: string; contractsActive: number; paidOffRate: number; recoveryContractsSummary: string }>({
    totalDebtRecovered: 0,
    paidOffSummary: '',
    contractsActive: 0,
    paidOffRate: 0,
    recoveryContractsSummary: ''
  });
  contractStatusData = signal<{ overdueContracts: number; financedBookPct: number; pausePayment: number }>({
    overdueContracts: 0,
    financedBookPct: 0,
    pausePayment: 0
  });

  remindersByChannelData = signal<{
    channel: string;
    percentage: number;
    failed: number;
    total: number;
    emailFailed: number;
  }>({
    channel: 'SMS',
    percentage: 0,
    failed: 0,
    total: 0,
    emailFailed: 0
  });

  supporterCapitalData = signal<{
    capitalFunded: number;
    disbursed: number;
    deployedPercent: number;
    subtitle: string;
    leftLabel: string;
    rightLabel: string;
  }>({
    capitalFunded: 0,
    disbursed: 0,
    deployedPercent: 0,
    subtitle: '',
    leftLabel: '',
    rightLabel: ''
  });

  supporterRepaymentData = signal<{
    repaid: number;
    returnedPercent: number;
    outstanding: number;
    subtitle: string;
  }>({
    repaid: 0,
    returnedPercent: 0,
    outstanding: 0,
    subtitle: ''
  });

  supporterFlowData = signal<{ funded: number; disbursed: number; outstanding: number }>({
    funded: 0,
    disbursed: 0,
    outstanding: 0
  });

  isSupporterCapitalLoading = signal(false);
  isSupporterRepaymentLoading = signal(false);
  isSupporterFlowLoading = signal(false);


  // Data mocks based on figma

  upfrontEligibilityData = signal<{ rate: number; total: number; eligible: number; notEligible: number }>({
    rate: 0,
    total: 0,
    eligible: 0,
    notEligible: 0
  });

  upfrontDisbursedData = signal<{
    declined: number;
    deferred: number;
    acceptanceRate: number;
    newAcceptance: number;
    newAdvances: number;
    totalEligible: number;
    totalAccepted: number;
    totalDisbursed: number;
    totalDisbursedFormatted: string;
    advancesCount: number;
    avgTicket: number;
  }>({
    declined: 0,
    deferred: 0,
    acceptanceRate: 0,
    newAcceptance: 0,
    newAdvances: 0,
    totalEligible: 0,
    totalAccepted: 0,
    totalDisbursed: 0,
    totalDisbursedFormatted: '$0',
    advancesCount: 0,
    avgTicket: 0
  });

  upfrontOutcomesData = signal<{ accepted: number; declined: number; deferred: number; notEligible: number; total: number }>({
    accepted: 0,
    declined: 0,
    deferred: 0,
    notEligible: 0,
    total: 0
  });

  portfolio = {
    overdueContracts: 609,
    financedBookPct: 8.0,
    pausePayment: 6,
    totalDebtRecovered: 4.49, // in Millions
    contractsPaidOff: 372,
    contractsActive: 875,
    paidOffRate: 42.5,
    lateFeeExposure: 102480,
    lateFeeInstances: 4039
  };

  ngOnInit() {
    // Initial fetch is driven by date picker `dateRangeChange`
  }

  fetchAllData() {
    if (!this.start_date || !this.end_date) return;

    if (!this.initialLoadDone) {
      this.loaderService.show();
    }
    this.fetchRemindersByChannel();
    this.fetchUpfrontEligibilityOverall();
    this.fetchUpfrontDisbursedOverall();
    this.fetchUpfrontOutcomes();
    this.fetchLateFeesAggregate();
    this.fetchDebtRecoveryOverall();
    this.fetchContractStatusByPeriod();
    this.fetchSupporterCapitalOverall();
    this.fetchSupporterRepaymentOverall();
    this.fetchSupporterFlow();
  }

  fetchRemindersByChannel() {
    this.isRemindersByChannelLoading.set(true);
    const productId = this.apiService.selectedProductId() || 'denefits';
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
    const toolsPath = productConfig.reportToolsPath || 'visionboard/tools';

    const payload = {
      start_date: this.start_date,
      end_date: this.end_date,
      unit: this.unit(),
      overall: 1,
      is_dummy: 0,
    };

    this.apiService.post(`${toolsPath}/reminders_by_channel`, payload, {
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
      showLoader: false,
      showErrorToaster: false
    })
      .pipe(finalize(() => this.isRemindersByChannelLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          let data = res?.data || res;
          if (Array.isArray(data)) {
            data = data[0] || {};
          }
          const sms = data?.channels?.sms || {};
          this.remindersByChannelData.set({
            channel: sms.channel || data?.worst_channel || 'SMS',
            percentage: Number(sms.failure_rate || data?.percentage || 0),
            failed: Number(sms.never_delivered || data?.failed || 0),
            total: Number(sms.pending || data?.total || 0),
            emailFailed: Number(sms.delivered || data?.emailFailed || 0)
          });
        },
        error: () => { }
      });
  }

  fetchUpfrontEligibilityOverall() {
    this.isUpfrontEligibilityLoading.set(true);
    const productId = this.apiService.selectedProductId() || 'denefits';
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
    const toolsPath = productConfig.reportToolsPath || 'visionboard/tools';

    const payload = {
      start_date: this.start_date,
      end_date: this.end_date,
      unit: this.unit(),
      overall: 1,
      is_dummy: 0,
    };

    this.apiService.post(`${toolsPath}/upfront_eligibility_overall`, payload, {
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
      showLoader: false,
      showErrorToaster: false
    })
      .pipe(finalize(() => this.isUpfrontEligibilityLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          let data = res?.data || res;
          if (Array.isArray(data)) {
            data = data[0] || {};
          }
          this.upfrontEligibilityData.set({
            rate: Number(data?.eligible_percent || data?.eligibility_rate || data?.rate || 0),
            total: Number(data?.total_contracts || data?.total_businesses || data?.total || 0),
            eligible: Number(data?.eligible_contracts || data?.eligible_businesses || data?.eligible || 0),
            notEligible: Number(data?.ineligible_contracts || data?.not_eligible_businesses || data?.not_eligible || 0)
          });
        },
        error: () => { }
      });
  }

  fetchUpfrontOutcomes() {
    this.isUpfrontOutcomesLoading.set(true);
    const productId = this.apiService.selectedProductId() || 'denefits';
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
    const toolsPath = productConfig.reportToolsPath || 'visionboard/tools';

    const payload = {
      start_date: this.start_date,
      end_date: this.end_date,
      unit: this.unit(),
      overall: 1,
      is_dummy: 0,
    };

    this.apiService.post(`${toolsPath}/upfront_outcomes`, payload, {
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
      showLoader: false,
      showErrorToaster: false
    })
      .pipe(finalize(() => this.isUpfrontOutcomesLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          let data = res?.data || res;
          if (Array.isArray(data)) {
            data = data[0] || {};
          }
          this.upfrontOutcomesData.set({
            accepted: Number(data?.accepted_contracts || data?.accepted || 0),
            declined: Number(data?.declined_contracts || data?.declined || 0),
            deferred: Number(data?.deferred_contracts || data?.deferred || 0),
            notEligible: Number(data?.not_eligible_contracts || data?.not_eligible || data?.notEligible || 0),
            total: Number(data?.total_contracts || data?.total || 0)
          });
        },
        error: () => { }
      });
  }

  fetchUpfrontDisbursedOverall() {
    this.isUpfrontDisbursedLoading.set(true);
    const productId = this.apiService.selectedProductId() || 'denefits';
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
    const toolsPath = productConfig.reportToolsPath || 'visionboard/tools';

    const payload = {
      start_date: this.start_date,
      end_date: this.end_date,
      unit: this.unit(),
      overall: 1,
      is_dummy: 0,
    };

    this.apiService.post(`${toolsPath}/upfront_disbursed_overall`, payload, {
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
      showLoader: false,
      showErrorToaster: false
    })
      .pipe(finalize(() => this.isUpfrontDisbursedLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          let data = res?.data || res;
          if (Array.isArray(data)) {
            data = data[0] || {};
          }
          let totalDisbursed = Number(data?.total_disbursed_amount || data?.total_disbursed || data?.disbursed || 0);
          let formattedDisbursed = `$${totalDisbursed.toFixed(2)}`;
          if (totalDisbursed >= 1000000) {
            formattedDisbursed = `$${(totalDisbursed / 1000000).toFixed(2)}M`;
          } else if (totalDisbursed >= 1000) {
            formattedDisbursed = `$${(totalDisbursed / 1000).toFixed(2)}K`;
          }

          this.upfrontDisbursedData.set({
            declined: Number(data?.declined_contracts || data?.declined || 0),
            deferred: Number(data?.deferred_contracts || data?.deferred || 0),
            acceptanceRate: Number(data?.accept_rate_percent || data?.acceptanceRate || 0),
            newAcceptance: Number(data?.new_acceptance || 50),
            newAdvances: Number(data?.new_advances || 300),
            totalEligible: Number(data?.total_eligible_contracts || data?.total_eligible || 0),
            totalAccepted: Number(data?.accepted_contracts || data?.total_accepted || data?.accepted || 0),
            totalDisbursed: totalDisbursed,
            totalDisbursedFormatted: formattedDisbursed,
            advancesCount: Number(data?.advances || data?.advances_count || 0),
            avgTicket: Number(data?.avg_ticket || 0)
          });
        },
        error: () => { }
      });
  }

  fetchContractStatusByPeriod() {
    this.isContractStatusLoading.set(true);
    const productId = this.apiService.selectedProductId() || 'denefits';
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
    const toolsPath = productConfig.reportToolsPath || 'visionboard/tools';

    const payload = {
      start_date: this.start_date,
      end_date: this.end_date,
      unit: this.unit(),
      overall: 1,
      is_dummy: 0,
    };

    this.apiService.post(`${toolsPath}/contract_status_by_period`, payload, {
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
      showLoader: false,
      showErrorToaster: false
    })
      .pipe(finalize(() => this.isContractStatusLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          let data = res?.data || res;
          if (typeof data === 'string') {
            try { data = JSON.parse(data); } catch (e) { }
          }
          if (Array.isArray(data)) {
            data = data[0] || {};
          }
          const summary = data?.summary || data || {};
          const active = Number(summary.active || 0);
          const overdue = Number(summary.overdue || 0);
          const completed = Number(summary.completed || 0);
          const cancelled = Number(summary.cancelled || 0);

          const total = active + overdue + completed + cancelled;
          const pct = total > 0 ? (overdue / total) * 100 : 0;

          this.contractStatusData.set({
            overdueContracts: total,
            financedBookPct: pct,
            pausePayment: Number(data?.pause_payment || data?.pausePayment || 0)
          });
        },
        error: () => { }
      });
  }

  fetchDebtRecoveryOverall() {
    this.isDebtRecoveryLoading.set(true);
    const productId = this.apiService.selectedProductId() || 'denefits';
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
    const toolsPath = productConfig.reportToolsPath || 'visionboard/tools';

    const payload = {
      start_date: this.start_date,
      end_date: this.end_date,
      unit: this.unit(),
      overall: 1,
      is_dummy: 0,
    };

    this.apiService.post(`${toolsPath}/debt_recovery_overall`, payload, {
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
      showLoader: false,
      showErrorToaster: false
    })
      .pipe(finalize(() => this.isDebtRecoveryLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          let data = res?.data || res;
          if (Array.isArray(data)) {
            data = data[0] || {};
          }

          let totalDebt = Number(data?.total_debt_recovered || 0);
          const summary = data?.paid_off_summary || '';
          const active = Number(data?.active_recovery_contracts || 0);
          const rate = Number(data?.paid_off_percent || 0);

          this.debtRecoveryData.set({
            totalDebtRecovered: totalDebt,
            paidOffSummary: summary,
            contractsActive: active,
            paidOffRate: rate,
            recoveryContractsSummary: data?.recovery_contracts_summary || ''
          });
        },
        error: () => { }
      });
  }

  fetchLateFeesAggregate() {
    this.isLateFeesLoading.set(true);
    const productId = this.apiService.selectedProductId() || 'denefits';
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
    const toolsPath = productConfig.reportToolsPath || 'visionboard/tools';

    const payload = {
      start_date: this.start_date,
      end_date: this.end_date,
      unit: this.unit(),
      overall: 1,
      is_dummy: 0,
    };

    this.apiService.post(`${toolsPath}/late_fees_aggregate`, payload, {
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
      showLoader: false,
      showErrorToaster: false
    })
      .pipe(finalize(() => this.isLateFeesLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          let data = res?.data || res;
          if (Array.isArray(data)) {
            data = data[0] || {};
          }
          this.lateFeesData.set({
            totalLateFees: Number(data?.total_late_fees || 0),
            instances: Number(data?.late_fee_instances || 0)
          });
        },
        error: () => { }
      });
  }

  fetchSupporterCapitalOverall() {
    this.isSupporterCapitalLoading.set(true);
    const productId = this.apiService.selectedProductId() || 'denefits';
    const productConfig = this.productConfigService.getProductConfig(this.productConfigService.getProductName(productId));
    const toolsPath = productConfig.reportToolsPath || 'visionboard/tools';
    const payload = { start_date: this.start_date, end_date: this.end_date, unit: this.unit(), overall: 1, is_dummy: 0 };

    this.apiService.post(`${toolsPath}/supporter_capital_overall`, payload, { addAccessToken: productConfig.requiresAccessToken, productServerName: productConfig.productServerName, showLoader: false, showErrorToaster: false })
      .pipe(finalize(() => this.isSupporterCapitalLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          let data = res?.data || res;
          if (Array.isArray(data)) data = data[0] || {};
          this.supporterCapitalData.set({
            capitalFunded: Number(data?.funded_capital || 0),
            disbursed: Number(data?.disbursed_to_businesses || 0),
            deployedPercent: Number(data?.percent_deployed || 0),
            subtitle: data?.subtitle || '',
            leftLabel: data?.progress_bar?.left_label || '',
            rightLabel: data?.progress_bar?.right_label || ''
          });
        },
        error: () => { }
      });
  }

  fetchSupporterRepaymentOverall() {
    this.isSupporterRepaymentLoading.set(true);
    const productId = this.apiService.selectedProductId() || 'denefits';
    const productConfig = this.productConfigService.getProductConfig(this.productConfigService.getProductName(productId));
    const toolsPath = productConfig.reportToolsPath || 'visionboard/tools';
    const payload = { start_date: this.start_date, end_date: this.end_date, unit: this.unit(), overall: 1, is_dummy: 0 };

    this.apiService.post(`${toolsPath}/supporter_repayment_overall`, payload, { addAccessToken: productConfig.requiresAccessToken, productServerName: productConfig.productServerName, showLoader: false, showErrorToaster: false })
      .pipe(finalize(() => this.isSupporterRepaymentLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          let data = res?.data || res;
          if (Array.isArray(data)) data = data[0] || {};
          this.supporterRepaymentData.set({
            repaid: Number(data?.returned_to_supporters || data?.repaid || data?.total_repaid || 0),
            returnedPercent: Number(data?.percent_returned || data?.returned_percent || data?.disbursed_returned_percent || 0),
            outstanding: Number(data?.outstanding || data?.total_outstanding || 0),
            subtitle: data?.subtitle || ''
          });
        },
        error: () => { }
      });
  }

  fetchSupporterFlow() {
    this.isSupporterFlowLoading.set(true);
    const productId = this.apiService.selectedProductId() || 'denefits';
    const productConfig = this.productConfigService.getProductConfig(this.productConfigService.getProductName(productId));
    const toolsPath = productConfig.reportToolsPath || 'visionboard/tools';
    const payload = { start_date: this.start_date, end_date: this.end_date, unit: this.unit(), overall: 1, is_dummy: 0 };

    this.apiService.post(`${toolsPath}/supporter_flow`, payload, { addAccessToken: productConfig.requiresAccessToken, productServerName: productConfig.productServerName, showLoader: false, showErrorToaster: false })
      .pipe(finalize(() => this.isSupporterFlowLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          let data = res?.data || res;
          if (Array.isArray(data)) data = data[0] || {};
          this.supporterFlowData.set({
            funded: Number(data?.funded || data?.total_funded || 0),
            disbursed: Number(data?.disbursed || data?.total_disbursed || 0),
            outstanding: Number(data?.outstanding || data?.total_outstanding || 0)
          });
        },
        error: () => { }
      });
  }

  private normalizeDate(date: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return dayjs.tz(date, LA_TIMEZONE).format('YYYY-MM-DD');
    }
    return dayjs(date).tz(LA_TIMEZONE).format('YYYY-MM-DD');
  }

  onDateRangeChange(event: DateRangePickerOutput) {
    const rangeKey = [
      event.start_date,
      event.end_date,
      event.comparison_type,
      event.previous_start_date ?? '',
      event.previous_end_date ?? '',
      event.unit
    ].join('|');

    if (rangeKey === this.lastDateRangeKey) return;
    this.lastDateRangeKey = rangeKey;

    this.start_date = this.normalizeDate(event.start_date);
    this.end_date = this.normalizeDate(event.end_date);
    this.comparisonType = event.comparison_type || 'none';

    if (this.comparisonType !== 'none' && event.previous_start_date && event.previous_end_date) {
      this.prev_start_date = this.normalizeDate(event.previous_start_date);
      this.prev_end_date = this.normalizeDate(event.previous_end_date);
    } else {
      this.prev_start_date = '';
      this.prev_end_date = '';
    }

    if (event.preset_label === 'Today' || event.preset_label === 'Yesterday' || this.start_date === this.end_date) {
      this.allowedGroupByUnits = ['hour', 'day', 'week', 'month', 'quarter', 'year'];
    } else {
      this.allowedGroupByUnits = ['day', 'week', 'month', 'quarter', 'year'];
    }

    this.unit.set(event.unit || this.unitService.getUnitSuggestions(this.start_date, this.end_date).recommended as any);
    this.fetchAllData();
  }

  timeToContractTotal = computed(() => {
    const data = this.upfrontOutcomesData();
    return data.accepted + data.declined + data.deferred + data.notEligible;
  });

  timeToContractDonut = computed(() => {
    const data = this.upfrontOutcomesData();
    const total = this.timeToContractTotal();
    const getPct = (val: number) => total ? (val / total * 100).toFixed(1) : '0';

    return [
      { label: 'Accepted', value: data.accepted, color: '#16a34a', percentage: getPct(data.accepted) },
      { label: 'Declined', value: data.declined, color: '#f59e0b', percentage: getPct(data.declined) },
      { label: 'Deferred', value: data.deferred, color: '#06b6d4', percentage: getPct(data.deferred) },
      { label: 'Not eligible', value: data.notEligible, color: '#94a3b8', percentage: getPct(data.notEligible) }
    ];
  });

  supporterFlowChartData = computed(() => {
    const data = this.supporterFlowData();
    const arr = [
      { label: 'Funded', value: data.funded, color: '#8b5cf6' },
      { label: 'Disbursed', value: data.disbursed, color: '#3b82f6' },
      { label: 'Outstanding', value: data.outstanding, color: '#f97316' }
    ];
    return arr.sort((a, b) => b.value - a.value);
  });

  rangeValues(event: any) {
    // Handle ranges
  }

  onCurrencyChange(value: string) {
    // Handle currency
  }

  formatToShort(num: number): string {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    }
    return num.toString();
  }
}
